import "./plan.scss"

import {
  Button,
  Checkbox,
  Layout,
  LegacyCard,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  TextContainer,
} from "@shopify/polaris"
import { ExternalIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import { observer } from "mobx-react"
import { InfoIcon, ProTooltip, useMediaQuery } from "polaris-enhance"
import { useEffect, useState } from "react"
import { PBAppToggle } from "react-pp-bundle"
import { useLocation } from "react-router"

import APIApp from "@/api/app"
import APIPlan from "@/api/plan"
import { AskedContent, FooterLink } from "@/components"
import V6MarketingBanner from "@/components/Banner/V6MarketingBanner"
import V6RecommendBanner from "@/components/Banner/V6RecommendBanner"
import V6RecommendBannerPC from "@/components/Banner/V6RecommendBannerPC"
import CrollSellModal from "@/components/CommonModel/CrollSellModal"
import { PLAN_QUESTION_LIST } from "@/constant"
import stores from "@/stores"
import { hasChosenPlan } from "@/utils/plan"
import toast from "@/utils/toast"

import {
  AllPlanList,
  EditSpendingLimitModal,
  FreeReturnServiceWidgetModal,
  Payment,
} from "./component"
import AccountInfoNew from "./component/AccountInfoNew"
import BundlePage from "./component/BundlePage"
import Styles from "./index.module.scss"

interface planProps {
  isSubscribeGuide: boolean
}

function Plan(props: planProps) {
  const { PlanStore, UserInfoStore } = stores
  const { isSubscribeGuide = false } = props
  const { type, appType } = PlanStore
  const location = useLocation<{
    tab: "returns" | "bundle"
    kind: number
  }>()
  const md = useMediaQuery("md")
  const hasChosenPlanFlag = hasChosenPlan()

  // 添加InfoIcon弹窗状态
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isSpendingLimitModalOpen, setIsSpendingLimitModalOpen] =
    useState(false)

  useEffect(() => {
    function resetState(state?: typeof location.state) {
      const packagePeriod =
        stores.PlanStore.config?.user_pkg.package_period ?? 1
      const appType =
        stores.PlanStore.config?.user_pkg?.source === 3 ? "bundle" : "returns"
      PlanStore.setAppType(state?.tab || appType)
      PlanStore.setPerferKind(state?.kind)
      PlanStore.setBundlePeriod(
        stores.PlanStore.bundleData?.user_pkg.default_plan_period ??
          packagePeriod,
      )
      PlanStore.setType(packagePeriod)
      PlanStore.updateCurrentStep()
    }
    if (PlanStore.bundleData) {
      resetState(location.state)
    }

    return () => {
      if (PlanStore.bundleData) {
        resetState()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PlanStore.bundleData])

  useEffect(() => {
    window.cio?.trigger({
      event: "view_plan_page",
    })
    window.cwreport?.report("view_plan_page")
  }, [])

  useEffect(() => {
    // 判断当前跳转页面url链接，是否有subscribe_success参数，提示订阅情况信息
    // 订阅成功状态 0-未订阅成功 1-升级订阅成功  2-降级订阅成功
    // todo 写个hoot获取url参数
    const urlParams = new URLSearchParams(window.location.search)
    const status = urlParams.get("subscribe_success")
    if (status === "1") {
      toast(t("UpgradedSuccessfully"), 4e3)
    } else if (status === "2") {
      toast(t("DowngradedSuccessfully"), 4e3)
    } else if (status === "0") {
      toast(t("ServerError"), 4e3, true)
    }
  }, [])

  // 切换plan页面，更新accountInfo数据，重新调
  useEffect(() => {
    PlanStore.updatePlanData()
  }, [])

  // if (!PlanStore.bundleData) return null

  return (
    <div
      className={classNames(
        "max-w-[1084px] mx-auto my-0 px-0 w-full mt-1",
        {
          "mt-[-4px]": isSubscribeGuide,
        },
        Styles.BillingContent,
        { [Styles.hidenTabs]: isSubscribeGuide },
      )}
    >
      <CrollSellModal />
      <EditSpendingLimitModal
        open={isSpendingLimitModalOpen}
        onClose={() => setIsSpendingLimitModalOpen(false)}
      />
      <Page fullWidth>
        {/* 组合套餐提示弹窗 */}
        {!isSubscribeGuide ? (
          <div className="flex justify-between items-center flex-wrap gap-2 mb-6">
            <div className="flex items-center gap-3">
              <Text variant="headingLg" as="h2">
                {t("Billing")}
              </Text>
              {PlanStore.showBannerData?.show_bundle && (
                <div className={"h-10 md:h-6 relative"}>
                  <div
                    className={
                      "md:absolute top-1/2 z-50 transform translate-x-0 md:-translate-y-1/2"
                    }
                    style={{
                      transform:
                        "translate(var(--tw-translate-x), var(--tw-translate-y))",
                    }}
                  >
                    <PBAppToggle
                      options={[
                        {
                          label: "ParcelWILL Returns",
                          value: "returns",
                        },
                        {
                          label: "ParcelWILL Bundle",
                          value: "bundle",
                        },
                      ]}
                      value={appType}
                      onChange={(value) => {
                        PlanStore.setAppType(value)
                        PlanStore.updateCurrentStep()
                        PlanStore.setPerferKind(undefined)
                        PlanStore.setPerferPlanId(undefined)
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {PlanStore.config?.user_pkg?.show_book_demo_button && (
                <Button
                  variant="secondary"
                  icon={ExternalIcon}
                  id="p-button-secondary"
                  onClick={() => {
                    APIApp.report({ event: 1 })
                    window.open(
                      "https://calendly.com/channelwill-parcelpanel/parcelpanel",
                      "_blank",
                    )
                  }}
                >
                  {t("BookADemo")}
                </Button>
              )}
              {(appType === "returns" ||
                !PlanStore.showBannerData?.show_bundle) &&
                hasChosenPlanFlag && (
                  <Button
                    id="p-button-secondary"
                    variant="tertiary"
                    onClick={() => setIsSpendingLimitModalOpen(true)}
                  >
                    {t("EditSpendingLimit")}
                  </Button>
                )}
            </div>
          </div>
        ) : null}

        {/* tab按钮切换：订阅和充值 */}
        <div>
          {(appType === "returns" ||
            !PlanStore.showBannerData?.show_bundle) && (
            <>
              {isSubscribeGuide && (
                <div className="mb-5 mt-1">
                  <div className="block sm:hidden">
                    <V6RecommendBanner />
                  </div>
                  <div className="hidden sm:block">
                    <V6RecommendBannerPC />
                  </div>
                </div>
              )}
              <Layout>
                {UserInfoStore.showBanner?.billing_upgrade === 1 &&
                  !isSubscribeGuide && (
                    <Layout.Section>
                      <V6MarketingBanner type="upgradeinplan" />
                    </Layout.Section>
                  )}
                {/* Account info 模块 */}
                {!isSubscribeGuide && (
                  <Layout.Section>
                    {PlanStore.loading ? (
                      <LegacyCard sectioned>
                        {Array.from({ length: 2 }).map((_, subIndex) => (
                          <div className="mb-2">
                            <TextContainer key={subIndex}>
                              <SkeletonDisplayText size="small" />
                              <SkeletonBodyText />
                            </TextContainer>
                          </div>
                        ))}
                      </LegacyCard>
                    ) : (
                      <AccountInfoNew />
                    )}
                  </Layout.Section>
                )}

                {/* 月、年套餐切换按钮 */}
                <Layout.Section>
                  <>
                    {PlanStore.loading ? (
                      <div className="flex justify-center">
                        <div className="w-[260px] h-[28px] p-4 bg-[#fff] rounded-xl pt-[10px] pb-[14px]">
                          <SkeletonBodyText lines={1} />
                        </div>
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          "flex items-center  flex-wrap gap-2",
                          {
                            "justify-between": md,
                            "justify-center": !md,
                          },
                        )}
                      >
                        {md && <div style={{ flex: 2 }}></div>}
                        <div
                          style={{ flex: 3 }}
                          className={classNames(
                            "flex items-center flex-wrap gap-2",
                            {
                              "justify-between": md,
                              "justify-center": !md,
                            },
                          )}
                        >
                          <div className="plan-billing-type-tabs">
                            <button
                              type="button"
                              className={classNames(
                                "plan-billing-type-tabs__tab",
                                {
                                  "plan-billing-type-tabs__tab--active":
                                    type === 1,
                                },
                              )}
                              onClick={() => {
                                PlanStore.setType(1)
                              }}
                            >
                              {t("PayMonthly")}
                            </button>
                            <button
                              type="button"
                              className={classNames(
                                "plan-billing-type-tabs__tab",
                                {
                                  "plan-billing-type-tabs__tab--active":
                                    type === 2,
                                },
                              )}
                              onClick={() => {
                                PlanStore.setType(2)
                              }}
                            >
                              {t("PayAnnuallySave20", {
                                replace: {
                                  percent: 20,
                                },
                              })}
                            </button>
                          </div>
                          {type === 1 && (
                            <div className="flex items-center gap-1">
                              <Checkbox
                                label={
                                  <ProTooltip
                                    content={t(
                                      "ReturnProtectionServiceLetsCustomers",
                                    )}
                                    width="wide"
                                    preferredPosition="below"
                                  >
                                    <span>{t("ReturnProtection")}</span>
                                  </ProTooltip>
                                }
                                checked={PlanStore.showReturnServiceShow === 1}
                                onChange={async (checked) => {
                                  const value = checked ? 1 : 2
                                  PlanStore.setShowReturnServiceShow(value)
                                  const { code } =
                                    await APIPlan.setShopExtraEnable(4, value)
                                  if (code !== 1000) {
                                    PlanStore.setShowReturnServiceShow(
                                      checked ? 2 : 1,
                                    )
                                  }
                                }}
                              />
                              <div className="w-5 h-5 relative shrink-0">
                                <Button
                                  variant="plain"
                                  icon={InfoIcon}
                                  size="micro"
                                  onClick={() => setIsInfoModalOpen(true)}
                                />
                                <FreeReturnServiceWidgetModal
                                  isOpen={isInfoModalOpen}
                                  onClose={() => setIsInfoModalOpen(false)}
                                  md={md}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                </Layout.Section>

                {/* 支付套餐部分 */}
                <Layout.Section>
                  {PlanStore.loading ? (
                    <div>
                      <div className="flex justify-between w-full gap-4">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div
                            className="w-full gap-4 flex flex-col bg-[#fff] p-4 rounded-xl"
                            key={index}
                          >
                            {Array.from({ length: 6 }).map((_, subIndex) => (
                              <TextContainer key={subIndex}>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText />
                              </TextContainer>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Payment type={type} isSubscribeGuide={isSubscribeGuide} />
                  )}
                </Layout.Section>
                {/* All plans 部分 */}
                <Layout.Section>
                  <AllPlanList />
                </Layout.Section>
                {/* question部分 */}
                <Layout.Section>
                  <AskedContent
                    type="collapsible"
                    subTitle={t("FrequentlyAskedQuestions")}
                    list={PLAN_QUESTION_LIST()}
                  />
                </Layout.Section>
              </Layout>
              <FooterLink />
            </>
          )}
          {appType === "bundle" && PlanStore.showBannerData?.show_bundle && (
            <BundlePage />
          )}
        </div>
      </Page>
    </div>
  )
}

export default observer(Plan)
